import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { getArticlesByChallengeFn } from "~/lib/functions/cih/articles";
import {
  getPublishedChallengeWithRelationsFn,
  type ChallengeWithRelations,
} from "~/lib/functions/cih/challenges";
import { createBrandMeta } from "~/lib/shared/meta";

type ArticleRow = Awaited<ReturnType<typeof getArticlesByChallengeFn>>[number];

type LoaderData = {
  title: string;
  challenge: ChallengeWithRelations;
  articles: ArticleRow[];
};

export const Route = createFileRoute(
  "/_public/coaching-for/$challengeSlug/articles",
)({
  staticData: { title: "Articles" },
  loader: async ({ params }): Promise<LoaderData> => {
    const [challenge, articles] = await Promise.all([
      getPublishedChallengeWithRelationsFn({
        data: { slug: params.challengeSlug },
      }),
      getArticlesByChallengeFn({
        data: { challengeSlug: params.challengeSlug },
      }),
    ]);
    if (!challenge) throw notFound();
    return {
      title: `Articles on ${challenge.title}`,
      challenge,
      articles,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { challengeSlug: string };
  }) => {
    const title = loaderData?.challenge.title ?? params.challengeSlug;
    return createBrandMeta({
      title: `${title} articles | Dream Coach Match`,
      description: `All expert-written articles on ${title}.`,
      ogUrl: `https://dreamcoachmatch.com/coaching-for/${params.challengeSlug}/articles`,
      canonical: `https://dreamcoachmatch.com/coaching-for/${params.challengeSlug}/articles`,
    });
  },
  component: ChallengeArticlesPage,
});

function ChallengeArticlesPage() {
  const { challenge, articles } = Route.useLoaderData();
  const { challengeSlug } = Route.useParams();

  const intakeSearch = {
    source: `challenge:${challengeSlug}:articles`,
    ...(challenge.intakeTrigger ? { trigger: challenge.intakeTrigger } : {}),
  };

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Coaching for", href: "/coaching-for" },
              {
                label: challenge.title,
                href: `/coaching-for/${challengeSlug}`,
              },
              { label: "Articles" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● {challenge.title}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            {articles.length} articles on {challenge.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <Link
              to="/coaching-for/$challengeSlug/coaches"
              params={{ challengeSlug }}
              className="font-medium text-purple-700 hover:text-purple-800"
            >
              See coaches who can help with {challenge.title} →
            </Link>
          </div>
        </div>
      </section>

      <div className="border-b border-gray-200">
        {articles.map(({ article, coach }) => (
          <Link
            key={article.id}
            to="/articles/$slug"
            params={{ slug: article.slug }}
            className="group grid grid-cols-1 items-baseline gap-2 border-b border-gray-200 px-4 py-6 transition-colors hover:bg-gray-50 md:grid-cols-[1fr_auto] md:gap-10 md:py-7 xl:px-0"
          >
            <div>
              <h3 className="text-base font-medium text-gray-900 md:text-lg">
                {article.title}
              </h3>
              <p className="mt-1 line-clamp-2 text-sm text-gray-600">
                {article.description}
              </p>
              <p className="mt-2 text-xs tracking-wider text-gray-500 uppercase">
                By{" "}
                <span className="text-gray-900">
                  {coach?.fullName ?? article.authorName}
                </span>
              </p>
            </div>
            <span className="hidden text-gray-400 group-hover:text-gray-900 md:inline">
              →
            </span>
          </Link>
        ))}
      </div>

      <CihClosingCta
        headline="Your next chapter starts with a question."
        cta={
          <Link to="/intake" search={intakeSearch}>
            <Button variant="primary" size="lg">
              Start the conversation →
            </Button>
          </Link>
        }
      />
    </>
  );
}
